import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, MapPin, Briefcase, Clock, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { useAuth } from "@/AuthContext";
import api from "@/api";
import { StarRating } from "@/components/CraftsmanCard";

export default function CraftsmanDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t, catName, money, cityName, jt, skillName } = useI18n();
  const [c, setC] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get(`/craftsmen/${id}`).then((r) => setC(r.data));
    api.get(`/craftsmen/${id}/reviews`).then((r) => setReviews(r.data));
  }, [id]);

  if (!c) return <div className="max-w-4xl mx-auto px-5 py-24 text-center text-[#A89A90]">…</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-[#EBDDD3] bg-white p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              {c.avatar && <img src={c.avatar} alt={c.name} className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover object-center shrink-0 border border-[#EBDDD3]" />}
              <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-display text-3xl font-black tracking-tight text-[#2D231F]">{c.name}</h1>
                {c.verified && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#5C6646]/10 text-[#5C6646] text-xs font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> {t("profile.verified")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-3 text-sm text-[#8C7A70] flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-[#C65338]/10 text-[#C65338] font-semibold">{catName(c.trade) || c.trade}</span>
                {c.city && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{cityName(c.city)}</span>}
                <StarRating value={c.rating} /> <span>({c.reviews} {t("profile.reviews")})</span>
              </div>
              <p className="mt-6 text-[#5A4B45] leading-relaxed">{jt(c, "bio")}</p>
              {c.skills?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {c.skills.map((s) => <span key={s} className="px-3 py-1 rounded-full bg-[#F2E8DF] text-[#5A4B45] text-sm">{skillName(s)}</span>)}
                </div>
              )}
              </div>
            </div>
          </div>

          {c.portfolio?.length > 0 && (
            <div className="rounded-2xl border border-[#EBDDD3] bg-white p-8">
              <h2 className="font-display text-xl font-bold text-[#2D231F] mb-4">{t("portfolio.title")}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {c.portfolio.map((url, i) => (
                  <img key={i} src={url} alt="" data-testid={`portfolio-img-${i}`} className="w-full h-32 object-cover rounded-lg" />
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-[#EBDDD3] bg-white p-8">
            <h2 className="font-display text-xl font-bold text-[#2D231F] mb-4">{t("admin.reviews")} ({reviews.length})</h2>
            {reviews.length === 0 ? (
              <p className="text-sm text-[#A89A90]">—</p>
            ) : (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r._id} className="border-b border-stone-100 pb-4 last:border-0" data-testid={`review-${r._id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[#3A2E28]">{r.customer_name}</span>
                      <StarRating value={r.rating} />
                    </div>
                    {r.text && <p className="text-sm text-[#5A4B45] mt-1">{jt(r, "text")}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-[#EBDDD3] bg-white p-6 space-y-4">
            {c.hourly_rate != null && (
              <div>
                <div className="font-display text-3xl font-black text-[#2D231F]">{money(c.hourly_rate)}<span className="text-base font-medium text-[#A89A90]">{t("common.perHour")}</span></div>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-[#5A4B45]"><Briefcase className="w-4 h-4 text-[#5C6646]" /> {c.jobs_completed} {t("profile.jobsDone")}</div>
            <div className="flex items-center gap-2 text-sm text-[#5A4B45]"><Clock className="w-4 h-4 text-[#5C6646]" /> {t("profile.responseTime")}</div>
            <div className="rounded-xl bg-[#F2E8DF] p-4 text-sm text-[#5A4B45] leading-relaxed" data-testid="contact-note">{t("profile.contactNote")}</div>
            {user?.role !== "craftsman" && (
              <Button data-testid="post-job-cta" onClick={() => nav(user ? "/post-job" : "/register")} className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 font-semibold gap-1.5"><PlusCircle className="w-4 h-4" />{t("profile.postJobCta")}</Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}